import styled from 'styled-components';
import { theme } from '../theme';

// Layout Container
export const LayoutContainer = styled.div`
  display: flex;
  min-height: 100vh;
  background-color: ${theme.colors.background.secondary};
`;

// Sidebar
export const Sidebar = styled.aside<{ $isOpen: boolean }>`
  width: ${props => props.$isOpen ? '280px' : '0'};
  background: ${theme.colors.background.primary};
  box-shadow: ${theme.shadows.lg};
  transition: width ${theme.transitions.normal};
  overflow: hidden;
  position: fixed;
  height: 100vh;
  z-index: ${theme.zIndex.sticky};

  @media (min-width: ${theme.breakpoints.md}) {
    position: relative;
    width: 280px;
  }
`;

export const SidebarHeader = styled.div`
  padding: ${theme.spacing[6]} ${theme.spacing[5]};
  border-bottom: 1px solid ${theme.colors.border.light};
`;

export const Logo = styled.h1`
  font-size: ${theme.typography.fontSize['2xl']};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.primary[600]};
  margin: 0;
  display: flex;
  align-items: center;
  gap: ${theme.spacing[3]};
`;

export const SidebarNav = styled.nav`
  padding: ${theme.spacing[4]} 0;
`;

export const NavSection = styled.div`
  margin-bottom: ${theme.spacing[6]};
`;

export const NavSectionTitle = styled.h3`
  font-size: ${theme.typography.fontSize.xs};
  font-weight: ${theme.typography.fontWeight.semibold};
  color: ${theme.colors.text.tertiary};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin: 0 0 ${theme.spacing[3]} 0;
  padding: 0 ${theme.spacing[5]};
`;

export const NavItem = styled.div<{ $isActive?: boolean; $hasSubmenu?: boolean }>`
  position: relative;
`;

export const NavLink = styled.button<{ $isActive?: boolean }>`
  width: 100%;
  display: flex;
  align-items: center;
  gap: ${theme.spacing[3]};
  padding: ${theme.spacing[3]} ${theme.spacing[5]};
  border: none;
  background: none;
  color: ${props => props.$isActive ? theme.colors.primary[600] : theme.colors.text.secondary};
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.medium};
  text-align: left;
  cursor: pointer;
  transition: all ${theme.transitions.fast};
  
  &:hover {
    background-color: ${theme.colors.gray[50]};
    color: ${theme.colors.primary[600]};
  }
  
  ${props => props.$isActive && `
    background-color: ${theme.colors.primary[50]};
    border-right: 3px solid ${theme.colors.primary[500]};
  `}
`;

export const NavSubmenu = styled.div<{ $isOpen?: boolean }>`
  background-color: ${theme.colors.gray[50]};
  overflow: hidden;
  max-height: ${props => props.$isOpen ? '500px' : '0'};
  transition: max-height ${theme.transitions.normal};
`;

export const NavSubmenuItem = styled.button<{ $isActive?: boolean }>`
  width: 100%;
  display: flex;
  align-items: center;
  gap: ${theme.spacing[3]};
  padding: ${theme.spacing[2]} ${theme.spacing[5]} ${theme.spacing[2]} ${theme.spacing[12]};
  border: none;
  background: none;
  color: ${props => props.$isActive ? theme.colors.primary[600] : theme.colors.text.secondary};
  font-size: ${theme.typography.fontSize.sm};
  text-align: left;
  cursor: pointer;
  transition: all ${theme.transitions.fast};
  
  &:hover {
    background-color: ${theme.colors.gray[50]};
    color: ${theme.colors.primary[600]};
  }
  
  ${props => props.$isActive && `
    color: ${theme.colors.primary[600]};
    font-weight: ${theme.typography.fontWeight.medium};
  `}
`;

// Main Content
export const MainContent = styled.main`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  
  @media (max-width: ${theme.breakpoints.md}) {
    margin-left: 0;
  }
`;

// Header
export const Header = styled.header`
  background: ${theme.colors.background.primary};
  border-bottom: 1px solid ${theme.colors.border.light};
  padding: 0 ${theme.spacing[6]};
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  z-index: ${theme.zIndex.docked};
`;

export const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[4]};
`;

export const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[4]};
`;

export const MobileMenuButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border: none;
  background: none;
  color: ${theme.colors.text.secondary};
  cursor: pointer;
  border-radius: ${theme.borderRadius.md};
  transition: all ${theme.transitions.fast};
  
  &:hover {
    background-color: ${theme.colors.gray[100]};
    color: ${theme.colors.text.primary};
  }
  
  @media (min-width: ${theme.breakpoints.md}) {
    display: none;
  }
`;

// User Menu
export const UserMenu = styled.div`
  position: relative;
`;

export const UserMenuButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[3]};
  padding: ${theme.spacing[2]} ${theme.spacing[3]};
  border: none;
  background: none;
  color: ${theme.colors.text.primary};
  cursor: pointer;
  border-radius: ${theme.borderRadius.md};
  transition: all ${theme.transitions.fast};
  
  &:hover {
    background-color: ${theme.colors.gray[100]};
  }
`;

export const UserAvatar = styled.div`
  width: 32px;
  height: 32px;
  border-radius: ${theme.borderRadius.full};
  background-color: ${theme.colors.primary[500]};
  color: ${theme.colors.text.inverse};
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: ${theme.typography.fontWeight.medium};
  font-size: ${theme.typography.fontSize.sm};
`;

export const UserInfo = styled.div`
  text-align: left;
`;

export const UserName = styled.div`
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.medium};
  color: ${theme.colors.text.primary};
`;

export const UserRole = styled.div`
  font-size: ${theme.typography.fontSize.xs};
  color: ${theme.colors.text.secondary};
`;

// Content Area
export const ContentArea = styled.div`
  flex: 1;
  padding: ${theme.spacing[6]};
  overflow-y: auto;
`;

// Mobile Overlay
export const MobileOverlay = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: ${theme.zIndex.overlay};
  opacity: ${props => props.$isOpen ? 1 : 0};
  visibility: ${props => props.$isOpen ? 'visible' : 'hidden'};
  transition: all ${theme.transitions.fast};
  
  @media (min-width: ${theme.breakpoints.md}) {
    display: none;
  }
`;
